import { queryKeys } from '@query/keys'
import { usePaginatedQuery } from '@query/usePaginatedQuery'
import { useQuery } from '@tanstack/react-query'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useArabicSessionLanguage, useSessionLanguage } from '@context/SessionSettingsContext'

import { Body2, Title1 } from '@ui/Typography'
import { PageSection } from '@ui/components/PageSection'
import { Pagination } from '@ui/v2/Pagination/Pagination'

import { PageQuery, SortOrder, useApiClient } from '@utils/ApiClient'
import { conditionalReverse } from '@utils/array'
import { ClientApiClient, emptyPaginationResponse } from '@utils/clientApi'
import { useDebouncedValue } from '@utils/debounce'

import { ClientIbContestCampaignDto } from '@model/ClientIbContestCampaignDto'
import { ClientIbContestCampaignResultDto } from '@model/ClientIbContestCampaignResultDto'
import { FilterQueryProps } from '@model/FilterFields'

import { useGenerateDocumentFile } from '@hooks/useGenerateDocumentFile'
import {
  ScrollToIds,
  useScrollAfterLoad,
  useScrollIntoViewOnPagingEntriesChange,
} from '@hooks/useScrollToElementIds'

import { IBContestResultsTable } from './IBContestResultsTable'

import styles from './IBContestPage.module.scss'

type IBContestSort = 'Rank' | 'IntroducingBroker.Name' | 'ClientCount' | 'ContestPoints'

const initialQuery = {
  From: undefined,
  To: undefined,
}

interface CampaignFetchProps {
  campaign: ClientIbContestCampaignDto
  search: SearchResponseProps
}

const useIbCampaignResultsFetch = (props: CampaignFetchProps) => {
  const { campaign, search } = props

  const locale = useSessionLanguage()
  const apiClient = useApiClient(ClientApiClient)
  const { account } = useAccountReadContext()

  const query = usePaginatedQuery({
    queryKey: queryKeys.ib.contestCampaignResults(campaign.id, {
      ...search.search,
      textSearch: search.debouncedSearchTerm,
      sort: search.pageQuery?.sort,
      sortOrder: search.pageQuery?.sortOrder,
    }),
    queryFn: (pageQuery) => {
      if (account?.id) {
        return apiClient.getIbContestByIdCampaignResults(campaign.id, {
          ...pageQuery,
          ...search.pageQuery,
          search: { ...search.search, ca_search_IbCode: search.debouncedSearchTerm },
          textSearch: search.debouncedSearchTerm,
          caller: 'ca',
          languageId: locale,
        })
      }
      return Promise.resolve(emptyPaginationResponse)
    },
    enabled: !!account?.id,
  })

  const { data: usersContestCampaignResult } = useQuery({
    queryKey: queryKeys.ib.userContestCampaign(campaign.id),
    queryFn: () => apiClient.getIbContestByIdCampaign(campaign.id),
    enabled: !!account?.id,
  })

  return { query, usersContestCampaignResult }
}

interface SearchResponseProps {
  search: FilterQueryProps
  setSearch(value: FilterQueryProps): void
  searchTerm: string | null
  setSearchTerm: React.Dispatch<React.SetStateAction<string | null>>
  debouncedSearchTerm: string | null
  pageQuery: PageQuery
  setPageQuery(value: PageQuery): void
}

const useSearch = (props: SortResponseProps): SearchResponseProps => {
  const { sort, sortOrder } = props

  const location = useLocation()

  const [search, setSearch] = useState<FilterQueryProps>(initialQuery)
  const [searchTerm, setSearchTerm] = useState<string | null>(null)
  const [pageQuery, setPageQuery] = useState<PageQuery>({ sort, sortOrder })
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 500)

  useEffect(() => {
    setSearch(initialQuery)
  }, [location.pathname])

  return {
    search,
    setSearch,
    searchTerm,
    setSearchTerm,
    debouncedSearchTerm,
    pageQuery,
    setPageQuery,
  }
}

interface SortResponseProps {
  getSortName(): string | undefined
  setSort(value: IBContestSort): void
  sort: IBContestSort
  sortOrder: SortOrder
  setSortOrder(value: SortOrder): void
  sortOptions: { id: string; name: string }[]
}

const useSort = (): SortResponseProps => {
  const { t } = useTranslation()

  const [sort, setSort] = useState<IBContestSort>('Rank')
  const [sortOrder, setSortOrder] = useState<SortOrder>('ASC')

  const sortOptions = [
    { id: 'Rank', name: t('IB.Ranking') },
    { id: 'IntroducingBroker.Name', name: t('IB.IB Username') },
    { id: 'ClientCount', name: t('IB.Number of Clients') },
    { id: 'ContestPoints', name: t('IB.Contest Points') },
  ]

  const getSortName = () => {
    return sortOptions.find((x) => x.id === sort)?.name
  }

  return {
    getSortName,
    setSort,
    sort,
    sortOrder,
    setSortOrder,
    sortOptions,
  }
}

const useIBContestResultsFile = (data: ClientIbContestCampaignResultDto[]) => {
  const { t } = useTranslation()
  const isArabic = useArabicSessionLanguage()

  const tableBody = () => {
    const headerCsvData = [
      conditionalReverse(
        [t('IB.Ranking'), t('IB.IB Username'), t('IB.Number of Clients'), t('IB.Contest Points')],
        isArabic
      ),
    ]

    return data.reduce(
      (previousValue, x) =>
        previousValue.concat([
          conditionalReverse(
            [`#${x.ranking}`, `${x.ibCode}`, x.numberOfClients.toString(), x.points.toString()],
            isArabic
          ),
        ]),
      headerCsvData
    )
  }

  return useGenerateDocumentFile('ib_contest-campaign_results', tableBody())
}

interface Props {
  campaign: ClientIbContestCampaignDto
  onGoBack(): void
}

export const IBContestResultsPage: React.FC<Props> = (props) => {
  const { campaign, onGoBack } = props
  const { t } = useTranslation()

  const sort = useSort()
  const search = useSearch(sort)
  const ibCampaignResults = useIbCampaignResultsFetch({
    search,
    campaign,
  })

  const generateDocumentFile = useIBContestResultsFile(ibCampaignResults.query.data)
  const [isPaginationEntrySelected, setIsPaginationEntrySelected] = useState(false)
  useScrollIntoViewOnPagingEntriesChange(
    ScrollToIds.IBContestResultsHeader,
    isPaginationEntrySelected,
    ibCampaignResults.query.isLoading,
    setIsPaginationEntrySelected
  )
  useScrollAfterLoad(
    ScrollToIds.IBContestResultsHeader,
    ibCampaignResults.query.isLoading,
    ibCampaignResults.query.meta?.pageSize
  )

  useEffect(() => {
    if (search.debouncedSearchTerm !== null) {
      ibCampaignResults.query.setPageQuery!((oldPageQuery: PageQuery) => ({
        ...oldPageQuery,
        pageIndex: 1,
      }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.debouncedSearchTerm])

  const handlePageChange = (pageIndex: number, pageSize?: number) => {
    if (pageSize && pageSize !== ibCampaignResults.query.meta?.pageSize) {
      setIsPaginationEntrySelected(true)
    }
    ibCampaignResults.query.setPageQuery!({
      ...ibCampaignResults.query.pageQuery,
      ...search.pageQuery,
      pageIndex,
      pageSize,
    })
  }

  const table = (
    <>
      <IBContestResultsTable
        tableQuery={ibCampaignResults.query}
        pageQuery={search.pageQuery}
        setPageQuery={search.setPageQuery}
      />
      {ibCampaignResults.query.meta && (
        <Pagination
          scrollToHeaderId={ScrollToIds.IBContestResultsHeader}
          maxPageSize={100}
          pageData={ibCampaignResults.query.meta}
          isLoading={ibCampaignResults.query.isLoading}
          onPageChanged={handlePageChange}
        />
      )}
    </>
  )

  return (
    <div>
      <PageSection
        title={campaign.name ?? ''}
        id={ScrollToIds.IBContestResultsHeader}
        documentFile={generateDocumentFile}
        onGoBack={onGoBack}
        searchField={{
          label: t('IB.Search by Username'),
          onChange: search.setSearchTerm,
          value: search.searchTerm,
          renderResult: () => table,
          canClear: true,
        }}
        renderMessage={() => (
          <div className={styles.message}>
            <Body2 className={styles.rankingLabel}>{t('IB.Current Ranking')}</Body2>
            {ibCampaignResults.usersContestCampaignResult?.ranking && (
              <Title1 className={styles.rankingValue}>
                #{ibCampaignResults.usersContestCampaignResult?.ranking}
              </Title1>
            )}
          </div>
        )}
      />
      {table}
    </div>
  )
}
